/** Small deterministic PRNG (mulberry32). */
export const createRandom = (seed: number): (() => number) => {
    let state = seed >>> 0

    return () => {
        state = (state + 0x6d2b79f5) >>> 0

        let t = state

        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

const GRAD: Array<[number, number]> = [
    [1, 1], [-1, 1], [1, -1], [-1, -1],
    [1, 0], [-1, 0], [0, 1], [0, -1],
    [0.7071, 0.7071], [-0.7071, 0.7071], [0.7071, -0.7071], [-0.7071, -0.7071]
]

const F2 = 0.5 * (Math.sqrt(3) - 1)
const G2 = (3 - Math.sqrt(3)) / 6

export type Noise2D = (x: number, y: number) => number

/** 2D simplex noise in the range of roughly -1..1. */
export const createNoise2D = (seed = 1): Noise2D => {
    const random = createRandom(seed)
    const perm = new Uint8Array(512)
    const base = new Uint8Array(256)

    for (let i = 0; i < 256; i++) base[i] = i

    for (let i = 255; i > 0; i--) {
        const j = Math.floor(random() * (i + 1))
        const swap = base[i]

        base[i] = base[j]
        base[j] = swap
    }

    for (let i = 0; i < 512; i++) perm[i] = base[i & 255]

    return (x: number, y: number): number => {
        const s = (x + y) * F2
        const i = Math.floor(x + s)
        const j = Math.floor(y + s)
        const t = (i + j) * G2
        const x0 = x - (i - t)
        const y0 = y - (j - t)
        const i1 = x0 > y0 ? 1 : 0
        const j1 = x0 > y0 ? 0 : 1
        const x1 = x0 - i1 + G2
        const y1 = y0 - j1 + G2
        const x2 = x0 - 1 + 2 * G2
        const y2 = y0 - 1 + 2 * G2
        const ii = i & 255
        const jj = j & 255

        let n0 = 0
        let n1 = 0
        let n2 = 0

        let t0 = 0.5 - x0 * x0 - y0 * y0

        if (t0 > 0) {
            const g = GRAD[perm[ii + perm[jj]] % 12]

            t0 *= t0
            n0 = t0 * t0 * (g[0] * x0 + g[1] * y0)
        }

        let t1 = 0.5 - x1 * x1 - y1 * y1

        if (t1 > 0) {
            const g = GRAD[perm[ii + i1 + perm[jj + j1]] % 12]

            t1 *= t1
            n1 = t1 * t1 * (g[0] * x1 + g[1] * y1)
        }

        let t2 = 0.5 - x2 * x2 - y2 * y2

        if (t2 > 0) {
            const g = GRAD[perm[ii + 1 + perm[jj + 1]] % 12]

            t2 *= t2
            n2 = t2 * t2 * (g[0] * x2 + g[1] * y2)
        }

        return 70 * (n0 + n1 + n2)
    }
}

/** Fractal brownian motion built on a 2D noise function. Output roughly -1..1. */
export const fbm = (noise: Noise2D, x: number, y: number, octaves = 5, lacunarity = 2, gain = 0.5): number => {
    let amplitude = 1
    let frequency = 1
    let sum = 0
    let norm = 0

    for (let i = 0; i < octaves; i++) {
        sum += amplitude * noise(x * frequency, y * frequency)
        norm += amplitude
        amplitude *= gain
        frequency *= lacunarity
    }

    return sum / norm
}

/** Ridged multifractal noise, good for sharp mountain crests. Output roughly 0..1. */
export const ridged = (noise: Noise2D, x: number, y: number, octaves = 6, lacunarity = 2.05, gain = 0.5): number => {
    let amplitude = 0.5
    let frequency = 1
    let sum = 0
    let weight = 1
    let norm = 0

    for (let i = 0; i < octaves; i++) {
        let signal = 1 - Math.abs(noise(x * frequency, y * frequency))

        signal *= signal
        signal *= weight
        weight = Math.min(1, Math.max(0, signal * 1.6))
        sum += signal * amplitude
        norm += amplitude
        amplitude *= gain
        frequency *= lacunarity
    }

    return sum / norm
}
